import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  approveOrder,
  getOrders,
  getOrdersByStatus,
  getAllVendors,
  fetchStateUsersWithOrders,
  formatNumber,
  getNewOrdersByCustomer,
} from "../../redux/thunk";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import OrderDetails from "../../components/modals/OrderDetails";
import axios from "axios";
import { RootState } from "../../redux/store";
import Pagination from "../../components/Pagination";
import { FaRegCopy } from "react-icons/fa6";
import { Preloader } from "../../components/elements/Preloader";
import { IoClose, IoSearchOutline } from "react-icons/io5";
import { formatDate } from "../../components/micro/Calender";
import DeliverySummaryModal from "../OrderTracking/DeliverySummaryModal";
import { useNavigate } from "react-router-dom";
import {
  canApproveOrder,
  getOrderDisplayStatus,
  orderStatusPillClass,
} from "../../utils/orderDisplayStatus";

interface StateGovernmentOrdersProps {
  categoryType: "lagos" | "stateGovernment";
  title: string;
}

const StateGovernmentOrdersComponent = ({
  categoryType,
  title,
}: StateGovernmentOrdersProps) => {
  const [viewDetails, setViewDetails] = useState(false);
  const [viewCancelOrder, setViewCancelOrder] = useState({
    details: {},
    status: false,
    btn: false,
  });
  const [cancelReason, setCancelReason] = useState("");
  const [orderDetails, setOrderDetails] = useState<any>({});
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [pickupDetails, setPickupDetails] = useState<any>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>("");
  const [error, setError] = useState<any>({});
  const [stateGovOrdersList, setStateGovOrdersList] = useState<any[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const [searchedUser, setSearchedUser] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeTab, setActiveTab] = useState("flexible");
  const { token } = useAppSelector((store: RootState) => store.auth);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [allOrdersForFilter, setAllOrdersForFilter] = useState<any[]>([]);
  const [monthlySummary, setMonthlySummary] = useState({
    label: "",
    totalOrders: 0,
    firstTimers: 0,
  });
  const [firstTimersOnly, setFirstTimersOnly] = useState(false);
  const [monthlyFirstTimerOrders, setMonthlyFirstTimerOrders] = useState<any[]>(
    [],
  );

  // Users with order page
  const [orderUsers, setOrderUsers] = useState({ totalResults: 0 });
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalResults, setTotalResults] = useState<number>(0);
  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;

  // Debounce the search input — only trigger data fetch after user stops typing
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchedUser.trim()) {
      setIsSearching(true);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchedUser.trim());
    }, 500);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchedUser]);

  const needsClientSideOrderCache =
    Boolean(deliveryDateFilter) ||
    Boolean(debouncedSearch) ||
    Boolean(selectedDate) ||
    activeTab === "comboPack";

  // Load all pages only when client-side filters need the full dataset (search, date, delivery).
  // Otherwise the table uses server pagination (page + limit 10) — avoids page=2&limit=100 churn.
  useEffect(() => {
    if (!needsClientSideOrderCache) {
      setAllOrdersForFilter([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const fetchAllOrdersForFilter = async () => {
      let fetchPage = 1;
      let totalPages = 1;
      let allOrders: any[] = [];
      do {
        if (cancelled) return;
        const res = await dispatch(
          getOrders({
            page: fetchPage,
            limit: 100,
            categoryType,
            orderType: activeTab,
          }),
        );
        if (res.payload.pagination && res.payload.pagination.results) {
          allOrders = [...allOrders, ...res.payload.pagination.results];
          totalPages = res.payload.pagination?.totalPages || 1;
        }
        fetchPage++;
      } while (fetchPage <= totalPages);
      if (!cancelled) {
        setAllOrdersForFilter(allOrders);
        setIsSearching(false);
      }
    };

    fetchAllOrdersForFilter();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    activeTab,
    categoryType,
    needsClientSideOrderCache,
    deliveryDateFilter,
    selectedDate,
  ]);

  useEffect(() => {
    setIsLoading(true);

    // Use backend status filtering when status filter is selected
    if (statusFilter) {
      dispatch(
        getOrdersByStatus({
          page: currentPage,
          limit: 10,
          categoryType,
          orderType: activeTab,
          status: statusFilter,
        }),
      ).then((response: any) => {
        setIsLoading(false);
        const res = response?.payload?.pagination ?? {};
        setStateGovOrdersList(res.results || []);
        setCurrentPage(res.currentPage || 1);
        setTotalPages(res.totalPages || 1);
        setTotalResults(res.totalResults || 0);
      });
    } else {
      // Default: fetch all orders without status filter
      dispatch(
        getOrders({
          page: currentPage,
          limit: 10,
          categoryType,
          orderType: activeTab,
        }),
      ).then((response: any) => {
        setIsLoading(false);
        const res = response?.payload?.pagination ?? {};
        setStateGovOrdersList(res.results || []);
        setCurrentPage(res.currentPage || 1);
        setTotalPages(res.totalPages || 1);
        setTotalResults(res.totalResults || 0);
      });
    }

    dispatch(getAllVendors());
  }, [dispatch, activeTab, currentPage, statusFilter]);

  // Filter for State Government Orders
  useEffect(() => {
    const fetchUsersWithOrders = async () => {
      const data = await fetchStateUsersWithOrders(1, 1, 10);
      setOrderUsers(data);
    };
    fetchUsersWithOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, statusFilter, deliveryDateFilter, searchedUser]);

  useEffect(() => {
    const fetchMonthlySummary = async () => {
      try {
        if (!selectedDate) {
          setMonthlySummary({ label: "", totalOrders: 0, firstTimers: 0 });
          setMonthlyFirstTimerOrders([]);
          setFirstTimersOnly(false);
          return;
        }
        const [yearStr, monthStr] = selectedDate.split("-");
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);
        const res = await dispatch(
          getNewOrdersByCustomer({ month, year }),
        ).unwrap();
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const label = monthNames[month - 1] || "";
        const firstTimerIds = new Set(
          (res.firstTimeOrdersCustomers || []).map((c: any) => c._id),
        );
        const firstTimerOrders = (res.orders || []).filter((o: any) => {
          const uid = o?.userId?.id || o?.userId?._id;
          return uid && firstTimerIds.has(uid);
        });
        setMonthlySummary({
          label,
          totalOrders: res.pagination?.totalOrders || res.orders?.length || 0,
          firstTimers: res.totalFirstTimeOrderCustomers || 0,
        });
        setMonthlyFirstTimerOrders(firstTimerOrders);
      } catch (err: any) {
        console.error("Monthly summary fetch error:", err);
        setMonthlySummary({ label: "", totalOrders: 0, firstTimers: 0 });
        setMonthlyFirstTimerOrders([]);
        setFirstTimersOnly(false);
      }
    };
    fetchMonthlySummary();
  }, [dispatch, selectedDate]);

  const validateOrderApproval = () => {
    let isValid = true;
    let newError: any = {};
    setError({});

    pickupDetails.forEach((item: any) => {
      if (!item.vendorId || item.vendorId.length === 0) {
        newError.pickupDetails = "Please select a vendor for all items.";
        isValid = false;
      }
    });
    if (!isValid) {
      setError(newError);
    }
    return isValid;
  };

  const handleCopyClick = (text: string | null | undefined) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const refreshOrders = async () => {
    const response: any = await dispatch(
      getOrders({
        page: 1,
        limit: 10,
        categoryType,
        orderType: activeTab,
      }),
    );
    const res = response?.payload?.pagination ?? {};
    setStateGovOrdersList(res.results);
    setCurrentPage(res.currentPage);
    setTotalPages(res.totalPages);
    setTotalResults(res.totalResults);
    return res.results as any[];
  };

  const handleInitialApprove = async (
    id: string,
    pickUpDetailsInput: string,
  ) => {
    setError({});
    if (pickUpDetailsInput.trim().length === 0) {
      setError({ initialPickup: "Enter pickup location" });
      return;
    }

    const payload = {
      orderId: id,
      pickUpDetails: pickUpDetailsInput,
    };

    setIsActionLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/approveOrderInitial`,
        payload,
        { headers: { Authorization: token } },
      );
      toast.success(res.data.message || "Order approved successfully");
      await refreshOrders();
      setViewDetails(false);
    } catch (error: any) {
      console.error("Initial Approve Error:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve order!";

      // Backend sometimes approves but responds 500; verify by refreshing list.
      const latest = await refreshOrders().catch(() => []);
      const updated = Array.isArray(latest)
        ? latest.find((o: any) => o?._id === id || o?.orderId === id)
        : null;

      if (updated && updated.status && updated.status !== "pending") {
        toast.success("Order approved successfully!");
        setViewDetails(false);
      } else {
        toast.error(message);
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleApproveOrder = async (id: string) => {
    if (!validateOrderApproval()) {
      return;
    }

    setIsActionLoading(true);
    try {
      const action = await dispatch(
        approveOrder({ orderId: id, pickUpDetails: pickupDetails }),
      );

      if (approveOrder.fulfilled.match(action)) {
        toast.success("Order approved successfully!");
      } else {
        const payload: any = action.payload || {};
        const status = payload?.status;
        const message = payload?.message;

        // Some upstream timeouts still result in the order being approved server-side.
        if (status === 502 || status === 503 || status === 504) {
          toast.success("Approval is processing. Refreshing orders...");
        } else {
          toast.error(message || "Failed to approve order!");
        }
      }

      await refreshOrders();
      setViewDetails(false);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (cancelReason.trim().length === 0) {
      toast.error("Please provide a reason for cancellation.");
      return;
    }

    setViewCancelOrder((prev) => ({ ...prev, btn: true }));
    setIsActionLoading(true);

    const payload = { cancelReason };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/cancelOrder/order/${orderId}`,
        payload,
        { headers: { Authorization: token } },
      );
      toast.success(res.data.message || "Order canceled successfully");
      Promise.resolve(
        dispatch(
          getOrders({
            page: 1,
            limit: 10,
            categoryType,
            orderType: activeTab,
          }),
        ).then((response: any) => {
          const res = response?.payload?.pagination ?? {};
          setStateGovOrdersList(res.results);
          setCurrentPage(res.currentPage);
          setTotalPages(res.totalPages);
          setTotalResults(res.totalResults);
        }),
      ); // Refresh orders
      setViewCancelOrder({ details: {}, status: false, btn: false });
      setCancelReason("");
    } catch (error: any) {
      console.error("Cancel Order error", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to cancel order",
      );
      setViewCancelOrder((prev) => ({ ...prev, btn: false }));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleViewDetails = (orderData: any) => {
    setOrderDetails(orderData);
    setViewDetails(true);
    setError({});
  };

  useEffect(() => {
    const getFilteredAndSearchedOrders = () => {
      // Use client-side cache when available, otherwise fall back to server data
      const sourceOrders = allOrdersForFilter.length > 0
        ? allOrdersForFilter
        : stateGovOrdersList;

      // Only special-case comboPack; other tabs are already filtered by API
      const isTabMatch = (order: any): boolean =>
        activeTab === "comboPack" ? Boolean(order.isXmasPackage) : true;

      const isDeliveryDateMatch = (order: any): boolean => {
        if (!deliveryDateFilter) return true;
        const deliveryDateOption =
          order.userId?.deliveryDateOption || order.deliveryDateOption;
        if (!deliveryDateOption) return false;

        const deliveryDate = new Date(deliveryDateOption);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (deliveryDateFilter === "today") {
          deliveryDate.setHours(0, 0, 0, 0);
          return deliveryDate.getTime() === today.getTime();
        }

        const days = parseInt(deliveryDateFilter.replace("day", ""), 10);
        if (!isNaN(days)) {
          const targetDate = new Date();
          targetDate.setDate(today.getDate() + days);
          targetDate.setHours(23, 59, 59, 999);
          return deliveryDate <= targetDate;
        }
        return true;
      };

      const isStatusMatch = (order: any): boolean =>
        !statusFilter || order.status === statusFilter;

      const isSearchMatch = (order: any): boolean => {
        const q = debouncedSearch.toLowerCase();
        if (!q) return true;
        const email = (order.userId?.email || "").toLowerCase();
        return email.includes(q);
      };

      const isDateMatch = (order: any): boolean => {
        if (!selectedDate) return true;
        const d = new Date(order.createdAt);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(d.getDate()).padStart(2, "0")}`;
        return key === selectedDate;
      };

      return sourceOrders
        .filter(isTabMatch)
        .filter(isDeliveryDateMatch)
        .filter(isStatusMatch)
        .filter(isSearchMatch)
        .filter(isDateMatch)
        .reverse();
    };

    setFilteredOrders(getFilteredAndSearchedOrders());
  }, [
    activeTab,
    deliveryDateFilter,
    allOrdersForFilter,
    stateGovOrdersList,
    debouncedSearch,
    selectedDate,
    statusFilter,
  ]);

  // Use filtered data when any client-side filter is active (including search)
  const hasNonStatusFilters = needsClientSideOrderCache || firstTimersOnly;

  const firstTimerItems = monthlyFirstTimerOrders.slice(
    indexOfFirstPost,
    indexOfLastPost,
  );
  const currentItems = filteredOrders.slice(indexOfFirstPost, indexOfLastPost);

  // Priority: firstTimersOnly > non-status filters (client-side) > server data
  const data = firstTimersOnly
    ? firstTimerItems
    : hasNonStatusFilters
      ? currentItems
      : stateGovOrdersList;

  const prevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // Pagination
  const goToPage = async (pageNumber: any) => {
    setSearchedUser("");
    setCurrentPage(pageNumber);
  };

  const handleTrackNow = (order: any) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  return (
    <main className="p-4 md:p-6">
      <h2 className="font-semibold text-xl mb-4">{title}</h2>

      {/* Tab Navigation */}
      <div className="my-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-5 border-b border-gray-200 pb-2">
          <span
            className={`cursor-pointer text-sm sm:text-base pb-2 ${
              activeTab === "flexible"
                ? "border-b-2 border-secondary font-semibold text-secondary"
                : "text-gray-600 hover:text-secondary"
            }`}
            onClick={() => setActiveTab("flexible")}
          >
            Flexible Orders
          </span>
          <span
            className={`cursor-pointer text-sm sm:text-base pb-2 ${
              activeTab === "outright"
                ? "border-b-2 border-secondary font-semibold text-secondary"
                : "text-gray-600 hover:text-secondary"
            }`}
            onClick={() => setActiveTab("outright")}
          >
            Outright Orders
          </span>
        </div>
      </div>

      {/* Filters Row */}
      <div className="my-6 flex flex-wrap items-center justify-between gap-4 bg-gray-50 p-4 rounded-md">
        <h3 className="text-md font-medium text-gray-700">
          Displaying: {totalResults} order(s)
          {selectedDate && (
            <span className="ml-4 text-gray-600">
              Monthly ({monthlySummary.label || "Selected Month"}):{" "}
              {monthlySummary.totalOrders}
            </span>
          )}
          {orderUsers?.totalResults > 0 ? (
            <button
              onClick={() =>
                navigate("users-with-orders", { state: { orderUsers } })
              }
              className="text-secondary hover:underline ml-5"
            >
              Users with Orders ({orderUsers?.totalResults})
            </button>
          ) : (
            <span className="text-secondary ml-5 opacity-50 cursor-default">
              Users with Orders ({orderUsers?.totalResults})
            </span>
          )}
          {selectedDate && (
            <button
              type="button"
              className="ml-4 text-secondary hover:underline"
              onClick={() => {
                if (monthlySummary.firstTimers > 0) {
                  setFirstTimersOnly(true);
                  setCurrentPage(1);
                }
              }}
            >
              First-time Orders: {monthlySummary.firstTimers}
            </button>
          )}
        </h3>
        <section className="flex flex-wrap items-center gap-4">
          {/* Search Input */}
          <div className="relative text-sm ">
            {isSearching ? (
              <div className="w-5 h-5 absolute top-1/2 left-3 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-secondary rounded-full animate-spin" />
              </div>
            ) : (
              <IoSearchOutline className="w-5 h-5 absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
            )}
            <input
              type="search"
              name="searchedUser"
              id="searchedUser"
              value={searchedUser}
              onChange={(e) => setSearchedUser(e.target.value)}
              placeholder="Search by client email..."
              className="border border-gray-300 p-2 rounded-md pl-10 w-full focus:outline-none focus:ring-1 focus:ring-secondary"
              disabled={totalResults === 0}
            />
          </div>

          {/* --- Delivery Date Filter --- */}
          <label
            htmlFor="deliveryDateFilter"
            className="flex items-center text-sm"
          >
            <span className="mr-2 text-gray-600">Delivery:</span>
            <select
              name="deliveryDateFilter"
              id="deliveryDateFilter"
              className="border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-secondary bg-white"
              value={deliveryDateFilter}
              onChange={(e) => setDeliveryDateFilter(e.target.value)}
            >
              <option value="">All Dates</option>
              <option value="today">Today</option>
              <option value="1day">1 Day</option>
              <option value="2day">2 Days</option>
              <option value="3day">3 Days</option>
              <option value="4day">4 Days</option>
              <option value="5day">5 Days</option>
              <option value="6day">6 Days</option>
              <option value="7day">7 Days</option>
            </select>
          </label>

          {/* Month Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm" htmlFor="selectedDate">
              Date Ordered:
            </label>
            <input
              type="date"
              name="selectedDate"
              id="selectedDate"
              className="border p-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-secondary"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <IoClose
              className={selectedDate ? "cursor-pointer" : "hidden"}
              onClick={() => setSelectedDate("")}
            />
          </div>

          {/* Status Filter */}
          <label htmlFor="statusFilter" className="flex items-center text-sm">
            <span className="mr-2 text-gray-600">Status:</span>
            <select
              name="statusFilter"
              id="statusFilter"
              className="border border-gray-300 p-2 ml-1 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-secondary bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="outForDelivery">Out for Delivery</option>
              <option value="atDeliveryLocation">At Delivery Location</option>
              <option value="delivered">Delivered</option>
              <option value="cancel">Canceled</option>
              <option value="rider">Rider</option>
            </select>
          </label>
        </section>
      </div>

      {/* Orders Table */}
      <section className="w-full bg-white p-3 rounded-lg shadow-md overflow-x-auto">
        <table className="w-full min-w-[900px]">
          {/* Adjusted min-width */}
          <thead className="text-left bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-3 text-sm font-semibold text-gray-600">SN</th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Client
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">Email</th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Order No.
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Total Price
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">Rider</th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Order Date
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Delivery Date
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Status
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">View</th>
              <th className="p-3 text-sm font-semibold text-gray-600">
                Action
              </th>
              <th className="p-3 text-sm font-semibold text-gray-600">Track</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading || isSearching ? (
              <tr>
                <td colSpan={12} className="text-center p-10">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : data && data.length > 0 ? (
              data.map((order: any, index: number) => (
                <tr
                  className="hover:bg-gray-50 text-sm"
                  key={order._id || index}
                >
                  <td className="p-3 font-medium text-secondary">
                    {index + indexOfFirstPost + 1}
                  </td>
                  <td className="p-3 capitalize">
                    {`${order.userId?.firstName?.toLowerCase() || ""} ${
                      order.userId?.lastName?.toLowerCase() || ""
                    }`.trim() || "N/A"}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 items-center">
                      {order.userId?.email && (
                        <button
                          type="button"
                          className="text-gray-400 hover:text-secondary"
                          title={`Copy ${order.userId.email}`}
                          onClick={() => handleCopyClick(order.userId.email)}
                        >
                          <FaRegCopy className="w-4 h-4" />
                        </button>
                      )}
                      <span>{order.userId?.email || "N/A"}</span>
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">
                    {order.orderNumber || "N/A"}
                  </td>
                  <td className="p-3 font-medium">
                    ₦
                    {formatNumber(
                      Math.round(Number(order.allItemsTotalPrice)) || 0,
                    )}
                  </td>
                  <td className="p-3 capitalize">
                    {`${order.riderId?.firstName?.toLowerCase() || "--"} ${
                      order.riderId?.lastName?.toLowerCase() || "--"
                    }` || "N/A"}
                  </td>
                  <td className="p-3">
                    {order.createdAt ? (
                      <span className="text-gray-500">
                        {formatDate(order.createdAt)}
                      </span>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="p-3">
                    {order?.deliveryDateOption ? (
                      <span className="text-gray-500">
                        {formatDate(
                          order?.deliveryDateOption ||
                            order?.userId?.deliveryDateOption,
                        )}
                      </span>
                    ) : (
                      "---"
                    )}
                  </td>

                  <td className="p-3">
                    {(() => {
                      const { label, styleKey } = getOrderDisplayStatus(order);
                      return (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${orderStatusPillClass(styleKey)}`}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-3">
                    <button
                      type="button"
                      className="px-3 py-1 bg-secondary text-white rounded text-xs hover:bg-secondary-dark transition-colors"
                      onClick={() => handleViewDetails(order)}
                    >
                      View Details
                    </button>
                  </td>
                  <td className="p-3">
                    <button
                      type="button"
                      className="px-3 py-1 rounded text-xs bg-gray-200 text-gray-500 cursor-not-allowed"
                      disabled
                      aria-disabled="true"
                      title="Cancel order is currently disabled"
                    >
                      Cancel Order
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleTrackNow(order)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Track Now
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center text-gray-500 p-6">
                  {searchedUser
                    ? "Customer not found."
                    : "No orders match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <section className="pt-4 mt-4 border-t border-gray-200">
          <Pagination
            length={
              firstTimersOnly
                ? monthlyFirstTimerOrders.length
                : hasNonStatusFilters
                  ? filteredOrders.length
                  : totalResults
            }
            itemsPerPage={itemsPerPage}
            handlePagination={goToPage}
            currentPage={currentPage}
            prevPage={prevPage}
            nextPage={nextPage}
            responseTotalPages={hasNonStatusFilters ? undefined : totalPages}
          />
          {firstTimersOnly && (
            <div className="mt-2 text-xs text-gray-600">
              Showing first-time orders for {monthlySummary.label}.{" "}
              <button
                type="button"
                className="text-secondary underline"
                onClick={() => {
                  setFirstTimersOnly(false);
                  setCurrentPage(1);
                }}
              >
                Clear
              </button>
            </div>
          )}
        </section>
      </section>

      {/* Cancel Order Modal */}
      {viewCancelOrder.status && (
        <div className="fixed z-50 inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">
              Cancel Order Confirmation
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for canceling this order:
            </p>
            <textarea
              name="reason"
              id="reason"
              className="w-full border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-secondary"
              placeholder="Enter reason..."
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-3 text-sm mt-5">
              <button
                type="button"
                className="py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                onClick={() => {
                  setCancelReason("");
                  setViewCancelOrder({
                    details: {},
                    btn: false,
                    status: false,
                  });
                }}
              >
                Back
              </button>
              <button
                type="button"
                className={`py-2 px-4 w-28 flex justify-center items-center rounded-md text-white transition-colors ${
                  cancelReason.trim().length === 0 ||
                  viewCancelOrder.btn ||
                  isActionLoading
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                onClick={() =>
                  handleCancelOrder((viewCancelOrder.details as any)?._id)
                }
                disabled={
                  cancelReason.trim().length === 0 ||
                  viewCancelOrder.btn ||
                  isActionLoading
                }
              >
                {viewCancelOrder.btn || isActionLoading ? (
                  <Preloader />
                ) : (
                  "Proceed"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {viewDetails && (
        <OrderDetails
          accountType="stateGovernment"
          orderDetails={orderDetails}
          setViewDetails={setViewDetails}
          pickupDetails={pickupDetails}
          setPickupDetails={setPickupDetails}
          handleApproveOrder={handleApproveOrder}
          handleInitialApprove={handleInitialApprove}
          error={error}
          setError={setError}
          isLoading={isActionLoading}
        />
      )}
      {showModal && (
        <DeliverySummaryModal
          selectedOrder={selectedOrder}
          onClose={() => setShowModal(false)}
        />
      )}
    </main>
  );
};

// Export Lagos State Government Orders
export const LagosStateGovernmentOrders = () => (
  <StateGovernmentOrdersComponent
    categoryType="lagos"
    title="Lagos State Government Orders"
  />
);

// Export Oyo State Government Orders
export const OyoStateGovernmentOrders = () => (
  <StateGovernmentOrdersComponent
    categoryType="stateGovernment"
    title="Oyo State Government Orders"
  />
);

// Keep old export for backward compatibility (can be removed later)
export const StateGovernmentOrders = LagosStateGovernmentOrders;
